import { useEffect, useState } from "react";
import axios from "axios";
import { css, keyframes } from "@emotion/react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  CircularProgress,
  Avatar,
  TextField,
} from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import * as adminUserAction from "../../actions/admin/user";
import { getProfileImageUrl } from "../../utils/serverUtils";
import EmailIcon from "@mui/icons-material/Email";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import JoinInnerIcon from "@mui/icons-material/JoinInner";
import TrafficIcon from "@mui/icons-material/Traffic";

import { API_URL } from "../../api";
import { tokens } from "../../theme";
import Header from "../../components/Header1";
import StatBox from "../../components/StatBox";
import PieChart from "../../components/PieChart";
import DatePickerInput from "../../components/DatePickerInput";

const agedata = [
  { age: "18-24", value: 6 },
  { age: "25-30", value: 3 },
  { age: "31-40", value: 4 },
  { age: "41+", value: 2 },
];

const cardStyle = {
  width: "100%",
  borderRadius: "12px",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  border: "1px solid #e0e0e0",
  overflow: "hidden",
};

const cardHeaderStyle = {
  background: "linear-gradient(90deg, #FC8C66, #F76A7B)",
  padding: "16px",
  textAlign: "center",
};

const cardContentStyle = {
  padding: "20px",
};

const cardTextStyle = {
  margin: "4px 0",
  fontWeight: "500",
  fontSize: "16px",
  textAlign: "center",
  color: "#333",
};

const StatCard = ({ title, value }) => (
  <Box display="flex" justifyContent="center" width="100%">
    <Card style={cardStyle}>
      <CardContent style={{ padding: 0 }}>
        <Box style={cardHeaderStyle}>
          <Typography variant="h7" style={{ color: "white", fontSize: "10px" }}>
            {title}
          </Typography>
        </Box>
        <Box style={cardContentStyle}>
          <Typography variant="body1" style={cardTextStyle}>
            {value}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  </Box>
);

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const listCardStyle = {
  width: "100%",
  maxWidth: "500px",
  margin: "16px",
  animation: `${fadeIn} 1s ease-in-out`,
};

const listCardHeaderStyle = {
  background: "linear-gradient(90deg, #FC8C66, #F76A7B)",
  padding: "16px",
};

const listCardContentStyle = {
  padding: "16px",
};

const itemStyle = index => css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 12px 0;
  padding: 8px;
  background-color: #f9f9f9;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  animation: ${slideIn} 0.5s ease-in-out ${index * 0.1}s forwards;
  opacity: 0;
`;

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [ageData, setAgedata1] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [genderData, setGenderData] = useState([]);
  const [totalNewUsersignup, setTotalNewUsersignup] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [TotalPeyment, setTotalPeyment] = useState(0);
  const [TotalLikeCount, setLikeCount] = useState(0);
  const [locationscount, setLocationsCount] = useState([]);
  const [CountTopJobs, setCountTopJobs] = useState([]);
  const [OldMembersSignedIn, setOldMembersSignedIn] = useState(0);
  const [TotalMatchsCount, setMatchsCount] = useState(0);
  const [TotalRequestCount, setRequestCount] = useState(0);
  const [AvgPaidConve, setAvgPaidConversations] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const [paidMemberCount, setPaidMemberCount] = useState(0);
  const [TotalRenewal, setTotalRenewalCount] = useState(0);
  const [AvgTime, setAvgTime] = useState("0m");
  const [timeRange, setTimeRange] = useState("month");
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getCountByGender = async () => {
    try {
      const params = { timeRange };
      if (timeRange === "custom") {
        params.startDate = startDate.toISOString().split("T")[0];
        params.endDate = endDate.toISOString().split("T")[0];
      }
      const response = await axios.get(
        `${API_URL}admin/dashboard/count-by-gender`,
        { params }
      );

      const transformedData = Object.keys(response.data).map(key => ({
        id: key,
        label: key,
        value: response.data[key],
      }));

      if (transformedData?.length === 0) {
        setGenderData([
          { id: "Men", label: "Men", value: 1 },
          { id: "Women", label: "Women", value: 1 },
          { id: "Other", label: "Other", value: 1 },
        ]);
      } else {
        setGenderData(transformedData);
      }
    } catch (error) {
      console.error("Error fetching count by gender:", error);
      setGenderData([
        { id: "Men", label: "Men", value: 1 },
        { id: "Women", label: "Women", value: 1 },
        { id: "Other", label: "Other", value: 1 },
      ]);
    }
  };

  const getAgeGroup = async () => {
    try {
      const params = { timeRange };
      if (timeRange === "custom") {
        params.startDate = startDate.toISOString().split("T")[0];
        params.endDate = endDate.toISOString().split("T")[0];
      }
      const response = await axios.get(
        `${API_URL}admin/dashboard/users/age-group`,
        { params }
      );

      const transformedData = response.data.map(item => ({
        id: item.age_group,
        label: item.age_group,
        value: item.count,
      }));

      if (transformedData?.length === 0) {
        setAgedata1([
          { id: "18-24", label: "18-24", value: 1 },
          { id: "25-30", label: "25-30", value: 1 },
          { id: "31-40", label: "31-40", value: 1 },
          { id: "41+", label: "41+", value: 1 },
        ]);
      } else {
        setAgedata1(transformedData);
      }
    } catch (error) {
      console.error("Error fetching age group data:", error);
      setAgedata1([
        { id: "18-24", label: "18-24", value: 1 },
        { id: "25-30", label: "25-30", value: 1 },
        { id: "31-40", label: "31-40", value: 1 },
        { id: "41+", label: "41+", value: 1 },
      ]);
    }
  };

  const TotalNewsignUp = async () => {
    try {
      const params = { timeRange };
      if (timeRange === "custom") {
        params.startDate = startDate.toISOString().split("T")[0];
        params.endDate = endDate.toISOString().split("T")[0];
      }
      const response = await axios.get(
        `${API_URL}admin/dashboard/users/count`,
        { params }
      );
      setTotalNewUsersignup(response.data);
    } catch (err) {}
  };

  const getRecentUsers = async () => {
    try {
      const queryParams = new URLSearchParams({
        limit: 100,
        pageNo: 1,
      });
      const response = await adminUserAction.getCustomerLists(queryParams);
      let users = response.results || [];
      
      // Filter by timeRange
      if (timeRange && timeRange !== '12months') {
        let start = moment().subtract(10, 'years');
        let end = moment();

        if (timeRange === 'custom' && startDate && endDate) {
             start = moment(startDate);
             end = moment(endDate).endOf('day');
        } else {
            switch(timeRange) {
               case '24h': start = moment().subtract(24, 'hours'); break;
               case 'week': start = moment().subtract(1, 'week'); break;
               case 'month': start = moment().subtract(1, 'month'); break;
               case '3months': start = moment().subtract(3, 'months'); break;
            }
        }
        
        users = users.filter(u => moment(u.created_at).isBetween(start, end));
      }

      // Sort by created_at desc
      users.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setRecentUsers(users);
    } catch (error) {
      console.error("Error fetching recent users:", error);
    }
  };

  const Transections = async () => {
    try {
      const response = await axios.get(`${API_URL}admin/dashboard/payments`);
      // Sort transactions by date descending
      const sortedTransactions = response.data.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setTransactions(sortedTransactions);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    }
  };

  const getMessageCount = async () => {
    try {
      const params = { timeRange };
      if (timeRange === "custom") {
        params.startDate = startDate.toISOString().split("T")[0];
        params.endDate = endDate.toISOString().split("T")[0];
      }
      const response = await axios.get(
        `${API_URL}admin/dashboard/messages/count`,
        { params }
      );
      setMessageCount(response.data.total_messages);
    } catch (error) {
      console.error("Error fetching message count:", error);
    }
  };

  const getLikeCount = async () => {
    try {
      const params = { timeRange };
      if (timeRange === "custom") {
        params.startDate = startDate.toISOString().split("T")[0];
        params.endDate = endDate.toISOString().split("T")[0];
      }
      const response = await axios.get(
        `${API_URL}admin/dashboard/likes/count`,
        { params }
      );
      setLikeCount(response.data.total_likes);
    } catch (error) {
      console.error("Error fetching like count:", error);
    }
  };

  const RequestCount = async () => {
    try {
      const params = { timeRange };
      if (timeRange === "custom") {
        params.startDate = startDate.toISOString().split("T")[0];
        params.endDate = endDate.toISOString().split("T")[0];
      }
      const response = await axios.get(
        `${API_URL}admin/dashboard/requests/count`,
        { params }
      );
      setRequestCount(response.data.total_requests);
    } catch (error) {
      console.error("Error fetching like count:", error);
    }
  };

  const getMatchCount = async () => {
    try {
      const params = { timeRange };
      if (timeRange === "custom") {
        params.startDate = startDate.toISOString().split("T")[0];
        params.endDate = endDate.toISOString().split("T")[0];
      }
      const response = await axios.get(
        `${API_URL}admin/dashboard/matches/count`,
        { params }
      );
      setMatchsCount(response.data.total_matches);
    } catch (error) {
      console.error("Error fetching message count:", error);
    }
  };

  const getTopJobs = async () => {
    try {
      const params = { timeRange };
      if (timeRange === "custom") {
        params.startDate = startDate.toISOString().split("T")[0];
        params.endDate = endDate.toISOString().split("T")[0];
      }
      const response = await axios.get(`${API_URL}admin/dashboard/top-jobs`, {
        params,
      });
      setCountTopJobs(response.data);
    } catch (error) {
      console.error("Error fetching message count:", error);
    }
  };

  const PaidMemberCount = async () => {
    try {
      const params = { timeRange };
      if (timeRange === "custom") {
        params.startDate = startDate.toISOString().split("T")[0];
        params.endDate = endDate.toISOString().split("T")[0];
      }
      const response = await axios.get(
        `${API_URL}admin/dashboard/new-paid-members/count`,
        { params }
      );
      setPaidMemberCount(response.data.total_new_paid_members);
    } catch (error) {
      console.error("Error fetching like count:", error);
    }
  };

  const TotalRenewalCount = async () => {
    try {
      const params = { timeRange };
      if (timeRange === "custom") {
        params.startDate = startDate.toISOString().split("T")[0];
        params.endDate = endDate.toISOString().split("T")[0];
      }
      const response = await axios.get(
        `${API_URL}admin/dashboard/renewals/count`,
        { params }
      );
      setTotalRenewalCount(response.data.results[0].total_renewals);
    } catch (error) {
      console.error("Error fetching like count:", error);
    }
  };

  const TotalOldMembersSignedIn = async () => {
    try {
      const params = { timeRange };
      if (timeRange === "custom") {
        params.startDate = startDate.toISOString().split("T")[0];
        params.endDate = endDate.toISOString().split("T")[0];
      }
      const response = await axios.get(
        `${API_URL}admin/dashboard/old-members/signed-in/count`,
        { params }
      );
      setOldMembersSignedIn(response.data.total_old_members_signed_in);
    } catch (error) {
      console.error("Error fetching like count:", error);
    }
  };

  const TotalPayments = async () => {
    try {
      const params = { timeRange };
      if (timeRange === "custom") {
        params.startDate = startDate.toISOString().split("T")[0];
        params.endDate = endDate.toISOString().split("T")[0];
      }
      const response = await axios.get(
        `${API_URL}admin/dashboard/payments/total`,
        { params }
      );
      setTotalPeyment(response.data.total_payments_cad.toFixed(1));
    } catch (error) {
      console.error("Error fetching message count:", error);
    }
  };

  const Toptencountry = async () => {
    try {
      const params = { timeRange };
      if (timeRange === "custom") {
        params.startDate = startDate.toISOString().split("T")[0];
        params.endDate = endDate.toISOString().split("T")[0];
      }
      const response = await axios.get(
        `${API_URL}admin/dashboard/top-countries`,
        { params }
      );
      setLocationsCount(response.data);
    } catch (error) {
      console.error("Error fetching message count:", error);
    }
  };

  const formatToK = amount => {
    if (amount >= 1000 && amount < 1000000) {
      return `${(amount / 1000).toFixed(1)}k`;
    } else if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M`;
    } else {
      return `${amount}`;
    }
  };

  const AvgPaidConversations = async () => {
    try {
      const params = { timeRange };
      if (timeRange === "custom") {
        params.startDate = startDate.toISOString().split("T")[0];
        params.endDate = endDate.toISOString().split("T")[0];
      }
      const response = await axios.get(
        `${API_URL}admin/dashboard/users/avg-days-to-paid`,
        { params }
      );
      setAvgPaidConversations(response.data);
    } catch (error) {
      console.error("Error fetching message count:", error);
    }
  };

  const AvgTimePerSession = async () => {
    try {
      const params = { timeRange };
      if (timeRange === "custom") {
        params.startDate = startDate.toISOString().split("T")[0];
        params.endDate = endDate.toISOString().split("T")[0];
      }
      const response = await axios.get(
        `${API_URL}admin/dashboard/sessions/avg-time`,
        { params }
      );
      // Convert seconds to readable format
      const seconds = response.data.avg_session_time;
      if (!seconds) {
        setAvgTime("0m");
        return;
      }
      const minutes = Math.floor(seconds / 60);
      setAvgTime(`${minutes} min`); 
    } catch (error) {
      console.error("Error fetching session time:", error);
      setAvgTime("N/A");
    }
  };

  useEffect(() => {
    const fetchDataall = async () => {
      try {
        setLoading(true);

        await Promise.all([
          getCountByGender(),
          getAgeGroup(),
          getRecentUsers(),
          TotalNewsignUp(),
          getMessageCount(),
          TotalPayments(),
          getLikeCount(),
          getMatchCount(),
          Toptencountry(),
          PaidMemberCount(),
          TotalRenewalCount(),
          RequestCount(),
          TotalOldMembersSignedIn(),
          getTopJobs(),
          AvgPaidConversations(),
          AvgTimePerSession(),
        ]);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchDataall();
  }, [timeRange, startDate, endDate]);

  useEffect(() => {
    Transections();
  }, []);

  // const formatToK = (num) => {
  //   if (num >= 1000) {
  //     return `${(num / 1000).toFixed(1)}k`;
  //   }
  //   return num.toString();
  // };
  const handleTimeRangeChange = event => {
    setTimeRange(event.target.value);
  };

  return (
    <>
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100dvh"
        >
          <Typography variant="body1" mr={2}>
            Hang tight, we're getting things ready for you!
          </Typography>
          <CircularProgress />
        </Box>
      ) : (
        <Box m="20px">
          {/* HEADER */}
          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            justifyContent="space-between"
            alignItems="center"
          >
            <Header title="DASHBOARD" subtitle="Welcome to your dashboard" />
            <Box mt={isMobile ? "10px" : "0"}>
              <Select
                value={timeRange}
                onChange={handleTimeRangeChange}
                width="50px"
                sx={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  padding: "8px",
                }}
              >
                <MenuItem value="24h">Last 24 Hours</MenuItem>
                <MenuItem value="week">Last Week</MenuItem>
                <MenuItem value="month">Last Month</MenuItem>
                <MenuItem value="3months">Last 3 Months</MenuItem>
                <MenuItem value="12months">Last 12 Months</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
              {timeRange === "custom" && (
                <Box display="flex" gap="10px" mt="10px" sx={{ 
                    "& .react-datepicker-wrapper input": {
                        padding: "8px",
                        borderRadius: "8px",
                        border: "1px solid #ccc",
                        width: "100px"
                    }
                }}>
                  <Box>
                    <DatePicker
                      selected={startDate}
                      onChange={(date) => setStartDate(date)}
                      selectsStart
                      startDate={startDate}
                      endDate={endDate}
                      popperClassName="datepicker-popper"
                      popperPlacement="bottom-start"
                      customInput={<DatePickerInput placeholder="Start Date" />}
                    />
                  </Box>
                  <Box>
                    <DatePicker
                      selected={endDate}
                      onChange={(date) => setEndDate(date)}
                      selectsEnd
                      startDate={startDate}
                      endDate={endDate}
                      minDate={startDate}
                      popperClassName="datepicker-popper"
                      popperPlacement="bottom-start"
                      customInput={<DatePickerInput placeholder="End Date" />}
                    />
                  </Box>
                  <style>{`
                    .datepicker-popper {
                      z-index: 9999 !important;
                    }
                  `}</style>
                </Box>
              )}
            </Box>
          </Box>
          {/* GRID */}
          <Box
            display="grid"
            gridTemplateColumns={isMobile ? "1fr" : "repeat(12, 1fr)"}
            gap="20px"
          >
            {/* ROW 1: Key KPIs */}
            <Box
              gridColumn={isMobile ? "span 12" : "span 3"}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <StatCard
                title="Total Revenue Generated"
                value={`CAD ${TotalPeyment}`}
              />
            </Box>
            <Box
              gridColumn={isMobile ? "span 12" : "span 3"}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
               <StatCard
                title="Total New Members Sign Up"
                value={totalNewUsersignup.total_users}
              />
            </Box>
            <Box
              gridColumn={isMobile ? "span 12" : "span 3"}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
               <StatCard
                title="Total of New Paid Members"
                value={paidMemberCount}
              />
            </Box>
            <Box
              gridColumn={isMobile ? "span 12" : "span 3"}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
               <StatCard
                title="Conversion Rate (Paid/New)"
                value={
                  totalNewUsersignup.total_users > 0
                    ? `${((paidMemberCount / totalNewUsersignup.total_users) * 100).toFixed(1)}%`
                    : "0%"
                }
              />
            </Box>

            {/* ROW 2: Charts & Lists */}
            <Box
              gridColumn={isMobile ? "span 12" : "span 6"}
              gridRow="span 2"
              backgroundColor="white"
              borderRadius="12px"
              p="30px"
              boxShadow="0 4px 12px rgba(0,0,0,0.1)"
            >
              <Typography variant="h5" fontWeight="600" mb="15px">
                Distribution of Genders
              </Typography>
              {genderData?.length !== 0 ? (
                <Box height="250px">
                  <PieChart data={genderData} />
                </Box>
              ) : (
                <Typography>No data</Typography>
              )}
            </Box>

             <Box
              gridColumn={isMobile ? "span 12" : "span 6"}
              gridRow="span 2"
              backgroundColor="white"
              borderRadius="12px"
              p="30px"
              boxShadow="0 4px 12px rgba(0,0,0,0.1)"
            >
               <Typography variant="h5" fontWeight="600" mb="15px">
                 Age Groups
              </Typography>
               {ageData?.length !== 0 ? (
                 <Box height="250px">
                   <PieChart data={ageData} />
                 </Box>
                ) : (
                  <Typography>No data</Typography>
                )}
            </Box>
            {/* Row 3 - KPIs */}
            <Box
              gridColumn={isMobile ? "span 12" : "span 4"}
              backgroundColor="white"
              borderRadius="12px"
              p="30px"
              boxShadow="0 4px 12px rgba(0,0,0,0.1)"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
               <StatCard title="Total of Renewals" value={TotalRenewal} />
            </Box>
            <Box
              gridColumn={isMobile ? "span 12" : "span 4"}
              backgroundColor="white"
              borderRadius="12px"
              p="30px"
              boxShadow="0 4px 12px rgba(0,0,0,0.1)"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
               <StatCard
                  title="Avg Days to Paid"
                  value={AvgPaidConve.avg_days_to_paid_conversion ? Math.round(AvgPaidConve.avg_days_to_paid_conversion) + " days" : "N/A"}
                />
            </Box>
             <Box
              gridColumn={isMobile ? "span 12" : "span 4"}
              backgroundColor="white"
              borderRadius="12px"
              p="30px"
              boxShadow="0 4px 12px rgba(0,0,0,0.1)"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
               <StatCard title="Avg Time Per Session" value={AvgTime} />
            </Box>

            {/* Row 4 - Recent New Sign Ups (Restored) */}
            <Box
              gridColumn="span 12"
              gridRow="span 2"
              height="400px"
              overflow="auto"
              backgroundColor="white"
              borderRadius="12px"
              boxShadow="0 4px 12px rgba(0,0,0,0.1)"
            >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  borderBottom={`4px solid ${colors.primary[500]}`}
                  p="15px"
                  position="sticky"
                  top="0"
                  zIndex="1"
                  backgroundColor="white"
                >
                  <Typography
                    color={colors.grey[100]}
                    variant="h6"
                    fontWeight="600"
                    sx={{ fontFamily: "Poppins, sans-serif" }}
                  >
                    Recent New Sign Ups
                  </Typography>
                </Box>
                {recentUsers.map((user, i) => (
                  <Box
                    key={`${user.user_id}-${i}`}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    borderBottom={`2px solid ${colors.primary[500]}`}
                    p="15px"
                    sx={{
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                   <Box display="flex" alignItems="center" gap="10px">
                      <Avatar
                        src={getProfileImageUrl(user)}
                        sx={{ width: 40, height: 40 }}
                      />
                      <Box>
                        <Typography
                          color={colors.greenAccent[500]}
                          variant="h5"
                          fontWeight="600"
                          sx={{ fontSize: "14px", fontFamily: "Poppins, sans-serif" }}
                        >
                          {user.first_name} {user.last_name}
                        </Typography>
                        <Typography
                          color={colors.grey[100]}
                          sx={{ fontSize: "12px", fontFamily: "Poppins, sans-serif" }}
                        >
                          {user.country || "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography
                        color={colors.grey[100]}
                        sx={{ fontSize: "12px", fontFamily: "Poppins, sans-serif" }}
                      >
                        {moment(user.created_at).fromNow()}
                      </Typography>
                    </Box>
                  </Box>
                ))}
            </Box>



            {/* Row 3 - Revenue Line Chart Removed */}
            
            {/* Row 3 continued - Transactions & Details */}
             {/* Row 3 continued - Transactions */}
             <Box
                gridColumn={isMobile ? "span 12" : "span 6"}
                gridRow="span 2"
                height="400px"
                overflow="auto"
                backgroundColor="white"
                borderRadius="12px"
                boxShadow="0 4px 12px rgba(0,0,0,0.1)"
              >
                 <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  borderBottom={`4px solid ${colors.primary[500]}`}
                  p="15px"
                  position="sticky"
                  top="0"
                  zIndex="1"
                  backgroundColor="white"
                >
                  <Typography
                    color={colors.grey[100]}
                    variant="h6"
                    fontWeight="600"
                    sx={{ fontFamily: "Poppins, sans-serif" }}
                  >
                    Recent Upgrades (Paid)
                  </Typography>
                </Box>
                {transactions.map((transaction, i) => (
                  <Box
                    key={`${transaction.id}-${i}`}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    borderBottom={`2px solid ${colors.primary[500]}`}
                    p="15px"
                    sx={{
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                     <Box>
                      <Typography
                        color={colors.greenAccent[500]}
                        variant="h5"
                        fontWeight="600"
                        sx={{ fontSize: "14px", fontFamily: "Poppins, sans-serif" }}
                      >
                         {transaction.first_name || transaction.id}
                      </Typography>
                      <Typography
                        color={colors.grey[100]}
                        sx={{ fontSize: "12px", fontFamily: "Poppins, sans-serif" }}
                      >
                        {moment(transaction.created_at).format("MMM D, YYYY")}
                      </Typography>
                    </Box>
                    <Box
                      backgroundColor={colors.greenAccent[500]}
                      p="5px 10px"
                      borderRadius="4px"
                      sx={{ fontFamily: "Poppins, sans-serif", color: "white", fontSize: "12px" }}
                    >
                      ${(transaction.amount / 100).toFixed(2)}
                    </Box>
                  </Box>
                ))}
            </Box>



            <Box
                gridColumn={isMobile ? "span 12" : "span 6"}
                backgroundColor="white"
                borderRadius="12px"
                p="20px"
                boxShadow="0 4px 12px rgba(0,0,0,0.1)"
                 overflow="auto"
            >
               <Typography variant="h6" fontWeight="600" mb="15px" borderBottom={`2px solid ${colors.primary[500]}`} pb="10px">
                 Top Locations
               </Typography>
               {locationscount.map((loc, i) => (
                   <Box key={i} display="flex" justifyContent="space-between" p="10px" borderBottom="1px solid #eee">
                      <Typography>{loc.country}</Typography>
                      <Box bgcolor={colors.greenAccent[500]} color="white" borderRadius="4px" px="8px">{loc.user_count}</Box>
                   </Box>
               ))}
            </Box>
          </Box>
          </Box>
      )}
    </>
  );
};

export default Dashboard;
